import React, { useEffect, useState } from 'react';
import styles from '../../assets/styles/Setting.module.scss';
import AccountSettingsLayout from '../../components/Setting/AccountSettingsLayout';
import { useUser } from '../../contexts/UserContext';

type CorporateSearchResult = {
  corporate_number: string;
  name: string;
  address: string;
  prefecture?: string;
  city?: string;
  street?: string;
  zip_code?: string;
};

type TenantInfo = {
  tenant_name: string;
  corporate_number: string | null;
  postal_code: string | null;
  prefecture: string | null;
  city: string | null;
  address1: string | null;
  address2: string | null;
  representative_tel: string | null;
};

// Mock Member Data
const MOCK_ACCOUNTS = [
  { id: 1, name: '山田 太郎', email: 'yamada@aibex.com', role: '管理者', status: '有効' },
  { id: 2, name: '佐藤 花子', email: 'sato@aibex.com', role: '一般', status: '有効' },
  { id: 3, name: '鈴木 一郎', email: 'suzuki@aibex.com', role: '一般', status: '招待中' },
];

const Corporate: React.FC = () => {
  const { user, fetchUser } = useUser();

  const [isEditingCompany, setIsEditingCompany] = useState(false);

  const [corporateNumber, setCorporateNumber] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [prefecture, setPrefecture] = useState('');
  const [city, setCity] = useState('');
  const [street, setStreet] = useState('');
  const [building, setBuilding] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [tenantInfo, setTenantInfo] = useState<TenantInfo | null>(null);
  const [isSavingCompany, setIsSavingCompany] = useState(false);
  const [noCorporateNumber, setNoCorporateNumber] = useState<boolean>(false);

  // 企業名検索用
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchTab, setSearchTab] = useState<'name' | 'number'>('name');
  const [searchNameQuery, setSearchNameQuery] = useState('');
  const [searchNumberQuery, setSearchNumberQuery] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<CorporateSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const setCompanyForm = (nextTenantInfo: TenantInfo) => {
    setCompanyName(nextTenantInfo.tenant_name || '');
    setCorporateNumber(nextTenantInfo.corporate_number || '');
    setZipCode(nextTenantInfo.postal_code || '');
    setPrefecture(nextTenantInfo.prefecture || '');
    setCity(nextTenantInfo.city || '');
    setStreet(nextTenantInfo.address1 || '');
    setBuilding(nextTenantInfo.address2 || '');
    setCompanyPhone(nextTenantInfo.representative_tel || '');
    if (!nextTenantInfo.corporate_number) {
      setNoCorporateNumber(true);
    } else {
      setNoCorporateNumber(false);
    }
  };

  const fetchTenantInfo = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/get-tenant-info`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('テナント情報取得に失敗しました');
      const data: TenantInfo = await res.json();
      setTenantInfo(data);
      if (!isEditingCompany) {
        setCompanyForm(data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!user?.tenant_id) return;
    fetchTenantInfo();
  }, [user?.tenant_id]);

  const toggleCompanyEdit = async () => {
    if (!isEditingCompany) {
      setIsEditingCompany(true);
      return;
    }

    if (!tenantInfo) {
      alert('企業情報が取得できていません。しばらくして再度お試しください。');
      return;
    }

    const nextTenantName = companyName.trim();
    const nextCorporateNumber = noCorporateNumber ? '' : corporateNumber.trim();
    const nextZipCode = zipCode.trim();
    const nextPrefecture = prefecture.trim();
    const nextCity = city.trim();
    const nextAddress1 = street.trim();
    const nextAddress2 = building.trim();
    const nextRepresentativeTel = companyPhone.trim();

    if (!nextTenantName) {
      alert('企業名（テナント名）を入力してください。');
      return;
    }

    const requiredAddressFields = [nextPrefecture, nextCity, nextAddress1];
    const filledAddressCount = requiredAddressFields.filter((value) => value).length;
    if (filledAddressCount !== 0 && filledAddressCount !== 3) {
      alert('都道府県・市区町村・番地・以降の住所は、すべて入力するか、すべて未入力にしてください。（郵便番号は任意です）');
      return;
    }

    const hasCompanyChange =
      nextTenantName !== (tenantInfo.tenant_name || '') ||
      nextCorporateNumber !== (tenantInfo.corporate_number || '') ||
      nextZipCode !== (tenantInfo.postal_code || '') ||
      nextPrefecture !== (tenantInfo.prefecture || '') ||
      nextCity !== (tenantInfo.city || '') ||
      nextAddress1 !== (tenantInfo.address1 || '') ||
      nextAddress2 !== (tenantInfo.address2 || '') ||
      nextRepresentativeTel !== (tenantInfo.representative_tel || '');

    if (!hasCompanyChange) {
      setCompanyForm(tenantInfo);
      setIsEditingCompany(false);
      return;
    }

    try {
      setIsSavingCompany(true);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/update-tenant-info`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_name: nextTenantName,
          corporate_number: nextCorporateNumber || null,
          postal_code: nextZipCode || null,
          prefecture: nextPrefecture || null,
          city: nextCity || null,
          address1: nextAddress1 || null,
          address2: nextAddress2 || null,
          representative_tel: nextRepresentativeTel || null,
        }),
      });

      if (!res.ok) {
        throw new Error('企業情報更新に失敗しました');
      }

      const updated: TenantInfo = await res.json();
      setTenantInfo(updated);
      setCompanyForm(updated);
      await fetchUser();
      setIsEditingCompany(false);
      alert('企業情報を更新しました');
    } catch (e) {
      console.error(e);
      alert('企業情報の更新に失敗しました');
    } finally {
      setIsSavingCompany(false);
    }
  };

  const handleAddressSearch = async () => {
    if (!zipCode || zipCode.length < 7) {
      alert('正しい郵便番号を入力してください');
      return;
    }
    try {
      const res = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${zipCode}`);
      const data = await res.json();
      if (data.results) {
        const result = data.results[0];
        setPrefecture(result.address1);
        setCity(result.address2);
        setStreet(result.address3);
      } else {
        alert('住所が見つかりませんでした');
      }
    } catch (e) {
      console.error(e);
      alert('住所検索に失敗しました');
    }
  };

  // モーダルを開くハンドラー
  const handleOpenModal = () => {
    if (!isEditingCompany) return;
    setShowSearchModal(true);
    setSearchNameQuery('');
    setSearchNumberQuery(null);
    setCandidates([]);
  };

  const handleSearchByName = async () => {
    if (searchNameQuery.trim().length < 2) {
      alert('2文字以上入力してください');
      return;
    }
    setIsSearching(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/nta/companies?name=${encodeURIComponent(searchNameQuery)}`);
      const data = await res.json();
      if (data.status === 'success' && data.data) {
        setCandidates(data.data);
      } else {
        setCandidates([]);
        alert('該当する企業が見つかりませんでした。');
      }
    } catch (error) {
      console.error("法人検索エラー", error);
      alert('検索中にエラーが発生しました。');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchByNumber = async () => {
    if (!searchNumberQuery || searchNumberQuery.trim().length !== 13) {
      alert('13桁の法人番号を入力してください');
      return;
    }
    setIsSearching(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/nta/companies/number?number=${encodeURIComponent(searchNumberQuery)}`);
      const data = await res.json();
      if (data.status === 'success' && data.data) {
        setCandidates(data.data);
      } else {
        setCandidates([]);
        alert('該当する企業が見つかりませんでした。');
      }
    } catch (error) {
      console.error("法人番号検索エラー", error);
      alert('検索中にエラーが発生しました。');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectCompany = (comp: CorporateSearchResult) => {
    setCompanyName(comp.name);
    setCorporateNumber(comp.corporate_number);
    if (comp.prefecture) setPrefecture(comp.prefecture);
    if (comp.city) setCity(comp.city);
    if (comp.street) setStreet(comp.street);
    setNoCorporateNumber(false);
    setShowSearchModal(false);
  };

  return (
    <AccountSettingsLayout>
      <div className={styles.twoColumnGrid}>

        {/* Left: Company Form */}
        <div className={styles.leftColumn}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>企業情報</h3>
              <button
                className={styles.actionButton}
                onClick={toggleCompanyEdit}
                disabled={isSavingCompany}
              >
                {isSavingCompany ? '保存中...' : isEditingCompany ? '保存' : '編集'}
              </button>
            </div>

            <div className={styles.formGroup}>
              <label>企業名（テナント名）</label>
              <div className={styles.searchGroup}>
                <input
                  type="text"
                  className={styles.inputField}
                  value={companyName}
                  onChange={() => { }}
                  onClick={handleOpenModal}
                  placeholder="クリックして法人を設定してください"
                  readOnly={true}
                  style={{ cursor: isEditingCompany ? 'pointer' : 'default', backgroundColor: isEditingCompany ? 'rgba(0, 0, 0, 0.3)' : undefined }}
                  disabled={!isEditingCompany}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>法人番号</label>
              <input
                type="text"
                className={styles.inputField}
                value={noCorporateNumber ? '' : corporateNumber}
                onChange={(e) => setCorporateNumber(e.target.value)}
                placeholder="検索結果から自動入力"
                maxLength={13}
                disabled={!isEditingCompany || noCorporateNumber}
              />
            </div>

            <div className={styles.formGroup}>
              <label>郵便番号</label>
              <div className={styles.addressRow}>
                <div className={styles.zipGroup}>
                  <input
                    type="text"
                    className={styles.inputField}
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    placeholder="123-4567"
                    disabled={!isEditingCompany}
                  />
                  {isEditingCompany && (
                    <button
                      type="button"
                      className={styles.miniButton}
                      onClick={handleAddressSearch}
                    >
                      自動入力
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.addressRow}>
              <div className={styles.formGroup}>
                <label>都道府県</label>
                <input
                  type="text"
                  className={styles.inputField}
                  value={prefecture}
                  onChange={(e) => setPrefecture(e.target.value)}
                  disabled={!isEditingCompany}
                />
              </div>
              <div className={styles.formGroup}>
                <label>市区町村</label>
                <input
                  type="text"
                  className={styles.inputField}
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  disabled={!isEditingCompany}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>番地・以降の住所</label>
              <input
                type="text"
                className={styles.inputField}
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                disabled={!isEditingCompany}
              />
            </div>

            <div className={styles.formGroup}>
              <label>建物名・部屋番号</label>
              <input
                type="text"
                className={styles.inputField}
                value={building}
                onChange={(e) => setBuilding(e.target.value)}
                disabled={!isEditingCompany}
              />
            </div>

            <div className={styles.formGroup}>
              <label>電話番号(代表)</label>
              <input
                type="tel"
                className={styles.inputField}
                value={companyPhone}
                onChange={(e) => setCompanyPhone(e.target.value)}
                placeholder="03-1234-5678"
                disabled={!isEditingCompany}
              />
            </div>
          </div>
        </div>

        {/* Right: Account List mock */}
        <div className={styles.rightColumn}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>所属アカウント一覧</h3>
              <button
                className={styles.actionButton}
                onClick={() => alert('ユーザー招待機能は現在開発中です')}
              >
                ＋ 招待する
              </button>
            </div>
            <div className={styles.tableWrap}>
              <table className={styles.historyTable}>
                <thead>
                  <tr>
                    <th>名前</th>
                    <th>権限</th>
                    <th>ステータス</th>
                  </tr>
                </thead>
                <tbody>
                  {MOCK_ACCOUNTS.map(acc => (
                    <tr key={acc.id}>
                      <td>
                        <div className={styles.textMedium}>{acc.name}</div>
                        <div className={styles.textSmallMuted}>{acc.email}</div>
                      </td>
                      <td>{acc.role}</td>
                      <td>
                        <span className={acc.status === '有効' ? styles.statusActive : styles.statusPending}>
                          {acc.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Search Result Modal */}
        {showSearchModal && (
          <div className={styles.modalOverlay} onClick={() => setShowSearchModal(false)}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h3>法人を検索</h3>
                <button className={styles.modalClose} onClick={() => setShowSearchModal(false)}>
                  ×
                </button>
              </div>

              <div className={styles.modalTabs}>
                <div
                  className={`${styles.modalTab} ${searchTab === 'name' ? styles.active : ''}`}
                  onClick={() => setSearchTab('name')}
                >企業名で検索</div>
                <div
                  className={`${styles.modalTab} ${searchTab === 'number' ? styles.active : ''}`}
                  onClick={() => setSearchTab('number')}
                >法人番号で検索</div>
              </div>

              {searchTab === 'name' ? (
                <div className={styles.modalSearchWrap}>
                  <input
                    type="text"
                    placeholder="企業名を入力 (例: Inovis)"
                    value={searchNameQuery}
                    onChange={(e) => setSearchNameQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSearchByName();
                    }}
                  />
                  <button onClick={handleSearchByName} disabled={isSearching}>検索</button>
                </div>
              ) : (
                <div className={styles.modalSearchWrap}>
                  <input
                    type="text"
                    placeholder="13桁の法人番号を入力"
                    value={searchNumberQuery || ''}
                    onChange={(e) => setSearchNumberQuery(e.target.value.replace(/[^0-9]/g, '').slice(0, 13) || null)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSearchByNumber();
                    }}
                  />
                  <button onClick={handleSearchByNumber} disabled={isSearching}>検索</button>
                </div>
              )}

              <div className={styles.modalResultContainer}>
                {isSearching ? (
                  <div className={styles.modalEmptyState}>検索中...</div>
                ) : candidates.length > 0 ? (
                  candidates.map((comp, idx) => (
                    <div
                      key={idx}
                      className={styles.modalResultItem}
                      onClick={() => handleSelectCompany(comp)}
                    >
                      <div className={styles.modalResultItemTitle}>{comp.name}</div>
                      <div className={styles.modalResultItemAddress}>{comp.address}</div>
                      <div className={styles.modalResultItemNumber}>法人番号: {comp.corporate_number}</div>
                    </div>
                  ))
                ) : (
                  <div className={styles.modalEmptyState}>
                    検索結果がここに表示されます。
                  </div>
                )}
              </div>

              <div className={styles.modalNoCorpWrap}>
                <label>
                  <input
                    type="checkbox"
                    checked={noCorporateNumber}
                    onChange={(e) => {
                      setNoCorporateNumber(e.target.checked);
                      if (e.target.checked) {
                        setCorporateNumber('');
                        setCompanyName('');
                      }
                    }}
                  />
                  法人番号を持たない・検索できない場合
                </label>
                {noCorporateNumber && (
                  <div className={styles.modalNoCorpInputWrap}>
                    <input
                      type="text"
                      placeholder="法人名または組織名を入力"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                    />
                    <button onClick={() => setShowSearchModal(false)}>決定</button>
                  </div>
                )}
              </div>

            </div>
          </div>
        )}

      </div>
    </AccountSettingsLayout>
  );
};

export default Corporate;
